import React, { Component } from 'react'
import classNames from 'classnames'

import { applyUpdate } from '../serviceWorkerRegistration'

import { UI } from '../config'
import '../styles/PwaPrompt.scss'

const INSTALL_DISMISS_KEY = 'ok.pwa.installDismissedAt'
const INSTALL_DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

const safeLocalStorage = {
  get(key) {
    try { return window.localStorage.getItem(key) } catch (e) { return null }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value) } catch (e) { /* quota / safari private */ }
  },
}

const isStandalone = () => {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches)
  ) || window.navigator.standalone === true
}

// One-stop shop for the three pieces of UX a PWA gains:
//   1. "New version available" prompt (driven by serviceWorkerRegistration's
//      onUpdate hook)
//   2. "Install app" CTA — listens for beforeinstallprompt, surfaces a
//      friendly button, defers until the user explicitly opts in or dismisses
//      (with a cooldown so we don't pester them).
//   3. Offline indicator — purely informational, fades in when the browser
//      reports offline.
// Mounted once at the root of the app (see ThemeContainer.js). Doesn't depend
// on Redux so it stays available even before the store hydrates.
export default class PwaPrompt extends Component {
  constructor(props) {
    super(props)
    this.state = {
      updateAvailable: false,
      installEvent: null,
      offline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
      offlineToastVisible: false,
    }
    this._mounted = false
    this._offlineToastTimer = null
  }

  componentDidMount() {
    this._mounted = true

    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)

    // beforeinstallprompt is fired by Chromium browsers when the PWA is
    // eligible for installation. We capture the event and defer it; calling
    // `prompt()` later requires this saved event.
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstall)
    window.addEventListener('appinstalled', this.handleAppInstalled)

    // Bridge from serviceWorkerRegistration so we don't have to thread props
    // — the registration sets a global flag when an update is waiting.
    window.__pwaShowUpdatePrompt = () => {
      if (this._mounted) this.setState({ updateAvailable: true })
    }
  }

  componentWillUnmount() {
    this._mounted = false
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstall)
    window.removeEventListener('appinstalled', this.handleAppInstalled)
    window.__pwaShowUpdatePrompt = undefined
    if (this._offlineToastTimer) clearTimeout(this._offlineToastTimer)
  }

  handleOnline = () => {
    if (!this._mounted) return
    this.setState({ offline: false, offlineToastVisible: true })
    if (this._offlineToastTimer) clearTimeout(this._offlineToastTimer)
    this._offlineToastTimer = setTimeout(() => {
      if (this._mounted) this.setState({ offlineToastVisible: false })
    }, 2500)
  }

  handleOffline = () => {
    if (!this._mounted) return
    this.setState({ offline: true, offlineToastVisible: true })
  }

  handleBeforeInstall = e => {
    e.preventDefault()
    const dismissedAt = parseInt(safeLocalStorage.get(INSTALL_DISMISS_KEY) || '0', 10)
    if (Date.now() - dismissedAt < INSTALL_DISMISS_COOLDOWN_MS) {
      return
    }
    if (isStandalone()) return
    this.setState({ installEvent: e })
  }

  handleAppInstalled = () => {
    safeLocalStorage.set(INSTALL_DISMISS_KEY, String(Date.now()))
    this.setState({ installEvent: null })
  }

  acceptInstall = () => {
    const { installEvent } = this.state
    if (!installEvent) return
    installEvent.prompt()
    Promise.resolve(installEvent.userChoice).then(() => {
      if (this._mounted) this.setState({ installEvent: null })
    })
  }

  dismissInstall = () => {
    safeLocalStorage.set(INSTALL_DISMISS_KEY, String(Date.now()))
    this.setState({ installEvent: null })
  }

  acceptUpdate = () => {
    applyUpdate()
  }

  dismissUpdate = () => {
    this.setState({ updateAvailable: false })
  }

  render() {
    const { updateAvailable, installEvent, offline, offlineToastVisible } = this.state

    return (
      <div className='PwaPrompt' role='region' aria-label='App status notifications'>
        {offline && (
          <div className='pwa-offline-bar' role='status' aria-live='polite'>
            {UI.OFFLINE_STATUS_MESSAGE}
          </div>
        )}

        {!offline && offlineToastVisible && (
          <div className='pwa-offline-bar pwa-offline-bar--restored' role='status' aria-live='polite'>
            Back online.
          </div>
        )}

        {updateAvailable && (
          <div className={classNames('pwa-toast', 'pwa-toast--update')} role='dialog' aria-live='polite'>
            <div className='pwa-toast-message'>
              A new version of Owner&apos;s Kit is available.
            </div>
            <div className='pwa-toast-actions'>
              <button type='button' className='pwa-toast-button pwa-toast-button--secondary' onClick={this.dismissUpdate}>
                Later
              </button>
              <button type='button' className='pwa-toast-button pwa-toast-button--primary' onClick={this.acceptUpdate}>
                Refresh
              </button>
            </div>
          </div>
        )}

        {installEvent && !updateAvailable && (
          <div className={classNames('pwa-toast', 'pwa-toast--install')} role='dialog' aria-live='polite'>
            <div className='pwa-toast-message'>
              Install Owner&apos;s Manuals for offline access and a full-screen experience.
            </div>
            <div className='pwa-toast-actions'>
              <button type='button' className='pwa-toast-button pwa-toast-button--secondary' onClick={this.dismissInstall}>
                Not now
              </button>
              <button type='button' className='pwa-toast-button pwa-toast-button--primary' onClick={this.acceptInstall}>
                Install
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }
}
